import { useCallback, useEffect, useMemo, useState } from 'react';

import { BaseScreen } from '../components/BaseScreen.tsx';
import ErrorMessage from '../components/ErrorMessage.tsx';
import { MainLayout } from '../components/MainLayout.tsx';
import MaintenanceScreen from '../components/MaintenanceScreen.tsx';
import { useMainContext } from '../context/MainContext.tsx';
import { useMergedConfig } from '../context/MergedConfigProvider.tsx';
import { PegaEventProvider } from '../context/PegaEventContext.tsx';
import { useMParticle } from '../hooks/useMparticle.ts';
import { useTranslations } from '../hooks/useTranslations.ts';
import { useSingpassCallback } from '../singpass/useSingpassCallback.ts';
import { translationKeys } from '../translations/translationKeys.ts';
import { readDevPegaToken } from '../utils/devPegaToken.ts';
import { getTranslatedApiError } from '../utils/getTranslatedApiError.ts';
import { PegaJourney } from './PegaJourney.tsx';
import { SignUpJourney } from './SignUpJourney.tsx';

const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

export const MainJourney = () => {
  const { pegaStartingFields, embedOneTimeToken, accountId, iaid, multiasset, product } = useMergedConfig();
  const { isLoading: singpassIsLoading, assignmentKey, errorCode } = useSingpassCallback();
  const { translate, isLoading: translationsIsLoading } = useTranslations();
  const isLoading = translationsIsLoading || singpassIsLoading;
  const { csrfToken, pegaToken, setPegaToken } = useMainContext();
  const [oneTimeToken, setOneTimeToken] = useState<string | undefined>();
  const [connectApplicationId, setConnectApplicationId] = useState<string | undefined>();
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [userPassword, setUserPassword] = useState<string | undefined>();

  const { setUserData } = useMParticle();

  const onSignUpJourneyCompleted = useCallback(
    (oneTimeToken: string, _connectApplicationId?: string, email?: string, password?: string) => {
      setOneTimeToken(oneTimeToken);
      setConnectApplicationId(_connectApplicationId);
      if (email) {
        setUserData({ email });
        setUserEmail(email);
      }
      if (password) setUserPassword(password);
    },
    [setUserData]
  );

  // let the webview know the application has started once translations are loaded
  useEffect(() => {
    if (!isLoading) {
      window.postMessage({ applicationStarted: true }, '*');
    }
  }, [isLoading]);

  const onPegaTokenExpiry = useCallback(() => {
    setOneTimeToken(undefined);
    setPegaToken(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // maybe move this to the config as well
  const startingFields = useMemo(
    () => ({
      ...pegaStartingFields,
      ConnectApplicationID: connectApplicationId,
      IAID: iaid,
      MultiAsset: multiasset,
      Product: product,
      AccountID: accountId,
    }),
    [connectApplicationId, pegaStartingFields, iaid, multiasset, product, accountId]
  );

  const devPegaToken = readDevPegaToken();
  const initiatedPegaJourney = oneTimeToken || embedOneTimeToken || devPegaToken || pegaToken;

  if (MAINTENANCE_MODE) {
    return (
      <MainLayout>
        <MaintenanceScreen
          message="This page is currently unavailable while we perform essential updates."
          estimatedTime="We expect to be back online in four hours. Stay in touch."
          showButton={true}
          buttonText="Close"
          buttonAction="close"
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {isLoading || errorCode ? (
        <BaseScreen
          applicationSubmitted={false}
          formTitle={''}
          footerBtnLabel={translate(translationKeys.FOOTER_BUTTON_LABEL)}
          footerBtnOnClick={() => {}}
          footerBtnLoading={false}
          footerBtnDisabled={true}
        >
          {errorCode ? (
            <ErrorMessage message={getTranslatedApiError(errorCode, translate)} />
          ) : (
            <div className="loading-overlay active" id="loadingOverlay">
              <div id="loaderContainer">
                <div className="loader-4"></div>
              </div>
            </div>
          )}
        </BaseScreen>
      ) : initiatedPegaJourney ? (
        <PegaEventProvider onPegaTokenExpiry={onPegaTokenExpiry}>
          <PegaJourney
            oneTimeToken={oneTimeToken}
            pegaToken={devPegaToken ?? pegaToken}
            pegaStartingFields={startingFields}
            embedOneTimeToken={embedOneTimeToken}
            iaid={iaid}
            csrfToken={csrfToken}
            userEmail={userEmail}
            userPassword={userPassword}
            assignmentKey={assignmentKey}
          />
        </PegaEventProvider>
      ) : (
        <SignUpJourney onCompleted={onSignUpJourneyCompleted} />
      )}
    </MainLayout>
  );
};
